/* Atlas v3 — Search overlay (window.AtlasSearch in the mockup).
   A slide-down sheet opened from the Overview header button and the
   Explore search field. Searches the active universe by ticker
   (prefix-weighted) + company name (substring); recents persisted in
   localStorage. THREE states: live results · no-results · empty/recent.

   It's an OVERLAY (position fixed, high z-index) — NOT a routed screen,
   so it carries no TickerStrip/BottomNav shell. Mount it inside the
   hosting screen's existing `.v3-root`.

   Data wiring:
     • universe   ← useAtlasStocks() → stocks (ticker / name / sector).
       Match = ticker prefix/substring OR company-name substring, ci.
       Prefix-on-ticker sorts first, then ticker-substring, then name.
     • recents    ← localStorage 'atlas_recent_search' (ticker strings),
       re-resolved against `stocks` for display; updated on select.
     • select     ← openTicker(symbol) then onClose().

   The mockup's per-row price/spark/change came from its SECTOR_STOCKS
   seed; atlas-data carries no price/spark, so rows show logo · ticker ·
   company · sector (matched substring emphasized) — same shape, minus
   the unavailable live fields. */
import { useState, useEffect, useMemo, useRef } from 'react'
import { REDUCE } from './lib'
import { StockLogo } from '@/v2/components/StockLogo'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const LS_KEY = 'atlas_recent_search'

function getRecents() {
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY) || '[]')
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []
  } catch { return [] }
}
function pushRecent(t) {
  try {
    const r = getRecents().filter((x) => x !== t)
    r.unshift(t)
    localStorage.setItem(LS_KEY, JSON.stringify(r.slice(0, 6)))
  } catch { /* localStorage unavailable */ }
}

/* emphasize the matched substring within a label */
function Highlight({ text, needle }) {
  if (!needle) return <>{text}</>
  const i = text.toLowerCase().indexOf(needle)
  if (i < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, i)}
      <span style={{ color: 'hsl(var(--studio-accent-soft))' }}>{text.slice(i, i + needle.length)}</span>
      {text.slice(i + needle.length)}
    </>
  )
}

function Row({ r, needle, onPick, first }) {
  const [hot, setHot] = useState(false)
  return (
    <div onClick={() => onPick(r)}
      onPointerDown={() => setHot(true)} onPointerUp={() => setHot(false)} onPointerCancel={() => setHot(false)} onPointerLeave={() => setHot(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 20px', cursor: 'pointer',
        background: hot ? 'hsl(var(--studio-surface-2) / 0.5)' : 'transparent',
        borderTop: first ? 'none' : '1px solid hsl(var(--studio-border) / 0.35)', transition: 'background 140ms var(--studio-ease)' }}>
      <StockLogo ticker={r.t} size={30} rounded={6} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...Mono, fontSize: 13, letterSpacing: '0.02em', color: hot ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text))', transition: 'color 160ms' }}>
          <Highlight text={r.t} needle={needle} />
        </div>
        <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-subtle))', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 230 }}>
          <Highlight text={r.n} needle={needle} />{r.sector ? ` · ${r.sector}` : ''}
        </div>
      </div>
      <span style={{ color: 'hsl(var(--studio-text-subtle))', fontSize: 16, flexShrink: 0 }}>›</span>
    </div>
  )
}

export function AtlasSearch({ open, onClose, openTicker }) {
  const [q, setQ] = useState('')
  const [shown, setShown] = useState(false)
  const inputRef = useRef(null)
  const { stocks, sectors, error: universeError } = useAtlasStocks()

  const universe = useMemo(
    () => (stocks || [])
      .filter((s) => s.ticker)
      .map((s) => ({ t: s.ticker, n: s.name || s.company || s.ticker, sector: s.sector || null })),
    [stocks],
  )

  // Real counts only — no invented "270 names / 12 sectors" when the
  // universe fetch failed or hasn't landed (seam ≠ fallback).
  const sectorCount = sectors?.length || 0
  const nameCount = universe.length

  // slide-in on open; reset query on close; focus + ESC handling.
  useEffect(() => {
    if (!open) { setShown(false); setQ(''); return }
    let raf
    if (!REDUCE) raf = requestAnimationFrame(() => setShown(true))
    else setShown(true)
    const focusId = setTimeout(() => inputRef.current && inputRef.current.focus(), REDUCE ? 0 : 120)
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => { if (raf) cancelAnimationFrame(raf); clearTimeout(focusId); window.removeEventListener('keydown', onKey) }
  }, [open, onClose])

  const needle = q.trim().toLowerCase()
  const results = useMemo(() => {
    if (!needle) return []
    return universe
      .filter((r) => r.t.toLowerCase().includes(needle) || r.n.toLowerCase().includes(needle))
      .sort((a, b) => {
        const at = a.t.toLowerCase(), bt = b.t.toLowerCase()
        const rank = (t) => (t.startsWith(needle) ? 0 : t.includes(needle) ? 1 : 2)
        return rank(at) - rank(bt) || at.localeCompare(bt)
      })
      .slice(0, 20)
  }, [universe, needle])

  const recentRows = useMemo(
    () => getRecents().map((t) => universe.find((s) => s.t === t)).filter(Boolean).slice(0, 5),
    [universe, open], // eslint-disable-line react-hooks/exhaustive-deps -- re-resolve recents each time the overlay reopens
  )

  if (!open) return null

  function pick(r) {
    pushRecent(r.t)
    openTicker && openTicker(r.t)
    onClose()
  }

  const reveal = shown || REDUCE

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.7)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', opacity: reveal ? 1 : 0, transition: 'opacity 200ms ease' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, maxHeight: '86%', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', borderBottom: '2px solid hsl(var(--studio-accent) / 0.5)', borderRadius: '0 0 16px 16px',
        boxShadow: '0 16px 44px hsl(var(--studio-overlay) / 0.6)', overflow: 'hidden',
        paddingTop: 'env(safe-area-inset-top)', // clear the iOS status bar (fixed overlay escapes .v3-root padding)
        transform: reveal ? 'translateY(0)' : 'translateY(-12px)', opacity: reveal ? 1 : 0,
        transition: 'transform 300ms var(--studio-ease), opacity 240ms var(--studio-ease)' }}>
        {/* input row */}
        <div style={{ flexShrink: 0, padding: '18px 20px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 9, padding: '10px 13px',
            background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius)' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--studio-text-subtle))" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ticker or company…"
              style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none', color: 'hsl(var(--studio-text))', ...Mono, fontSize: 13.5, letterSpacing: '0.02em' }} />
            {q && <button onClick={() => setQ('')} aria-label="Clear search" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--studio-text-subtle))', fontSize: 13, padding: 0, lineHeight: 1 }}>✕</button>}
          </div>
          <button onClick={onClose} style={{ ...Mono, fontSize: 10.5, letterSpacing: '0.06em', background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--studio-accent-soft))', padding: '4px 0', flexShrink: 0 }}>CANCEL</button>
        </div>
        {/* body */}
        <div className="atlas-scroll" style={{ flex: 1, overflowY: 'auto', paddingBottom: 18 }}>
          {needle ? (
            results.length ? (
              <div>
                <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', padding: '8px 20px 6px' }}>{results.length} MATCH{results.length === 1 ? '' : 'ES'}</div>
                {results.map((r, i) => <Row key={r.t} r={r} needle={needle} onPick={pick} first={i === 0} />)}
              </div>
            ) : (
              <div style={{ padding: '38px 20px', textAlign: 'center' }}>
                <div style={{ ...Display, fontSize: 18, color: 'hsl(var(--studio-text-muted))' }}>Nothing in the universe</div>
                <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', marginTop: 8, lineHeight: 1.6 }}>
                  {nameCount > 0
                    ? <>ATLAS TRACKS {nameCount} NAMES ACROSS {sectorCount} SECTORS.<br />&quot;{q.toUpperCase()}&quot; ISN&apos;T ONE OF THEM YET.</>
                    : <>{universeError ? 'THE UNIVERSE FEED IS UNAVAILABLE RIGHT NOW.' : 'LOADING THE UNIVERSE…'}</>}
                </div>
              </div>
            )
          ) : recentRows.length > 0 ? (
            <div>
              <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', padding: '10px 20px 6px' }}>RECENT</div>
              {recentRows.map((r, i) => <Row key={r.t} r={r} onPick={pick} first={i === 0} />)}
            </div>
          ) : (
            <div style={{ padding: '44px 28px', textAlign: 'center' }}>
              <div style={{ ...Display, fontSize: 18, color: 'hsl(var(--studio-text-muted))' }}>Find any name</div>
              <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', marginTop: 8, lineHeight: 1.6 }}>
                {nameCount > 0
                  ? <>SEARCH {nameCount} TICKERS ACROSS {sectorCount} SECTORS<br />BY SYMBOL OR COMPANY NAME.</>
                  : <>{universeError ? 'THE UNIVERSE FEED IS UNAVAILABLE RIGHT NOW.' : 'SEARCH BY SYMBOL OR COMPANY NAME.'}</>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AtlasSearch
